import * as React from 'react'
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/ui/avatar'

function rng(seed: number) {
  return (n: number) => {
    const x = Math.sin(seed * 9301 + n * 49297) * 233280
    return x - Math.floor(x)
  }
}

function getInitials(name: string | null | undefined): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function generateAvatarSvg(seed: number, initials: string, size: number): string {
  const r = rng(seed)
  const h1 = Math.floor(r(1) * 360)
  const h2 = (h1 + 30 + Math.floor(r(2) * 60)) % 360
  const sat = 55 + r(3) * 25
  const l1 = 42 + r(4) * 10
  const l2 = 58 + r(5) * 10
  const angle = Math.floor(r(6) * 360)
  const accentOpacity = 0.18 + r(7) * 0.18
  const accentRot = Math.floor(r(8) * 180)
  const fontSize = Math.round(size * 0.42)
  const radius = Math.round(size * 0.22)
  const gid = `sa-${seed}`
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
    <defs>
      <linearGradient id="${gid}" gradientTransform="rotate(${angle} 0.5 0.5)">
        <stop offset="0%" stop-color="hsl(${h1},${sat.toFixed(0)}%,${l1.toFixed(0)}%)"/>
        <stop offset="100%" stop-color="hsl(${h2},${sat.toFixed(0)}%,${l2.toFixed(0)}%)"/>
      </linearGradient>
      <clipPath id="${gid}-c"><rect width="${size}" height="${size}" rx="${radius}"/></clipPath>
    </defs>
    <rect width="${size}" height="${size}" rx="${radius}" fill="url(#${gid})"/>
    <g clip-path="url(#${gid}-c)">
      <rect x="${-size}" y="${size * 0.55}" width="${size * 3}" height="${size * 0.18}" fill="white" opacity="${accentOpacity.toFixed(2)}" transform="rotate(${accentRot} ${size / 2} ${size / 2})"/>
    </g>
    <text x="50%" y="50%" text-anchor="middle" dominant-baseline="central" fill="white" font-family="Inter, system-ui, sans-serif" font-weight="600" font-size="${fontSize}" letter-spacing="-0.5">${initials}</text>
  </svg>`
}

interface StudioAvatarProps {
  /** Stable seed for deterministic colour identity. Pass `teamMember.id`. */
  seed: number | string
  /** Used to derive the initials shown on the fallback. */
  name: string | null | undefined
  /** Real photo URL — when present, overrides the generated avatar. */
  imageUrl?: string | null
  /** Pixel size. Determines the rendered SVG resolution. */
  size?: number
  /** Extra class names forwarded to the outer Avatar primitive. */
  className?: string
}

export function StudioAvatar({ seed, name, imageUrl, size = 40, className }: StudioAvatarProps) {
  const seedNum = typeof seed === 'number' ? seed : (
    // Stable string→number hash for non-numeric seeds
    Array.from(String(seed)).reduce((a, c) => ((a * 31 + c.charCodeAt(0)) | 0), 0)
  )
  const initials = React.useMemo(() => getInitials(name), [name])
  const svgUrl = React.useMemo(
    () => `data:image/svg+xml;utf8,${encodeURIComponent(generateAvatarSvg(seedNum, initials, size))}`,
    [seedNum, initials, size]
  )
  return (
    <Avatar className={className} style={{ width: size, height: size }}>
      {imageUrl && <AvatarImage src={imageUrl} alt={name ?? ''} />}
      <AvatarFallback className="p-0">
        <img src={svgUrl} alt={name ?? 'Avatar'} width={size} height={size} style={{ display: 'block' }} />
      </AvatarFallback>
    </Avatar>
  )
}
